import React from 'react';
import { ClosingTasks } from 'sellDirect/helpers/closingTasks';
import { WizardSummary, getTaskDetailRow } from 'sellDirect/components/closingDash/WizardSummary';
import { text } from 'sellDirect/components/closingDash/content';
import { UnderContractOffer } from 'sellDirect/OfferContext';
import { SPRINKLER_TIMER_TYPE_MAP } from 'sellDirect/helpers/closingDash/disclosures';
import { SprinklerTimer } from '__generated__/globalTypes';

export interface DisclosuresSummaryProps {
  offer: UnderContractOffer;
  closingTasks: ClosingTasks;
  setIsEditingForm: (isEditingForm: boolean) => void;
}

const FOUNDATION_EVALUATION_MAP: { [key: string]: string } = {
  '0_to_2_years': '0-2 years',
  '2_to_5_years': '2-5 years',
  '5_plus_years': '5+',
  dont_know: "I don't know / I have not had it evaluated",
};

const HEATING_SYSTEM_TO_DISPLAY_NAME: { [key: string]: string } = {
  central_heating_furnace: 'Central heating - furnace',
  central_heating_boiler: 'Central heating - boiler',
  solar_heating: 'Solar heating',
  electric_heating: 'Electric heating',
  electric_baseboards: 'Electric baseboards',
  other: 'Other',
  not_sure: 'Not sure',
  none: 'None',
};

const COOLING_SYSTEM_TO_DISPLAY_NAME: { [key: string]: string } = {
  central_forced_air: 'Central forced air',
  wall_window_unit: 'Wall/window unit',
  evaporative_coolers: 'Evaporative coolers',
  other: 'Other',
  not_sure: 'Not sure',
  none: 'None',
};

/**
 * Renders WizardSummary for disclosures form.
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const DisclosuresSummary: React.FC<DisclosuresSummaryProps> = ({
  offer,
  closingTasks,
  setIsEditingForm,
}) => {
  const hvacRoofInfo = closingTasks.hvacRoofInfo?.taskable;
  const serviceAccountsTask = closingTasks.serviceAccounts?.taskable;
  const otherDisclosures = closingTasks.otherHomeInfo?.taskable;
  const shouldAskPolybutenePiping = offer.closingExperience?.shouldAskPolybutenePiping;
  const shouldAskStormRoofDamage = offer.closingExperience?.shouldAskStormAndFoundation;
  const pumpRows = [
    getTaskDetailRow(
      text.disclosuresPage.hvacRoofInfo.hasSumpPump(offer.shouldSeeAgentExperience),
      hvacRoofInfo?.hasSumpPump,
    ),
    getTaskDetailRow(
      text.disclosuresPage.hvacRoofInfo.hasSprinkler(offer.shouldSeeAgentExperience),
      hvacRoofInfo?.hasSprinklerOrIrrigation,
    ),
  ];

  if (hvacRoofInfo?.hasSprinklerOrIrrigation) {
    pumpRows.push(
      getTaskDetailRow(
        text.disclosuresPage.hvacRoofInfo.sprinklerTimerType,
        hvacRoofInfo?.sprinklerTimerType
          ? SPRINKLER_TIMER_TYPE_MAP[hvacRoofInfo?.sprinklerTimerType]
          : SPRINKLER_TIMER_TYPE_MAP[SprinklerTimer.UNKNOWN],
      ),
      getTaskDetailRow(
        text.disclosuresPage.hvacRoofInfo.sprinklerInWorkingOrder,
        hvacRoofInfo?.workingSprinkler,
      ),
    );
  }

  const claimRows = [
    getTaskDetailRow(
      text.disclosuresPage.hvacRoofInfo.hasFiledInsuranceClaim(offer.shouldSeeAgentExperience),
      hvacRoofInfo?.filedClaim,
    ),
  ];

  if (hvacRoofInfo?.filedClaim) {
    claimRows.push(
      getTaskDetailRow(
        text.disclosuresPage.hvacRoofInfo.claimDetails,
        hvacRoofInfo?.damageClaimDetails,
      ),
      getTaskDetailRow(
        text.disclosuresPage.hvacRoofInfo.receivedProceedsFromClaim(offer.shouldSeeAgentExperience),
        hvacRoofInfo?.receivedProceeds,
      ),
    );
  }

  const waterRows = [
    getTaskDetailRow(
      text.disclosuresPage.hvacRoofInfo.hasWellWaterSupply(offer.shouldSeeAgentExperience),
      hvacRoofInfo?.hasWellWaterSupply,
    ),
  ];

  if (shouldAskPolybutenePiping) {
    waterRows.push(
      getTaskDetailRow(
        text.disclosuresPage.hvacRoofInfo.hasPolybutenePiping(offer.shouldSeeAgentExperience),
        hvacRoofInfo?.hasPolybutenePiping,
      ),
    );
  }

  const roofAndFoundationRows = shouldAskStormRoofDamage
    ? [
        getTaskDetailRow(
          text.disclosuresPage.hvacRoofInfo.hasRoofStormDamage(offer.shouldSeeAgentExperience),
          hvacRoofInfo?.roomStormDamage,
        ),
        getTaskDetailRow(
          text.disclosuresPage.hvacRoofInfo.lastFoundationEvaluation(
            offer.shouldSeeAgentExperience,
          ),
          hvacRoofInfo?.lastFoundationEvaluation
            ? FOUNDATION_EVALUATION_MAP[hvacRoofInfo?.lastFoundationEvaluation]
            : 'N/A',
        ),
      ]
    : [];

  const hvacRoofRows = [...pumpRows, ...claimRows, ...waterRows, ...roofAndFoundationRows];

  const heatingAndCoolingRows = [
    getTaskDetailRow(
      text.disclosuresPage.serviceAccounts.serviceAccountHeatingType(
        offer.shouldSeeAgentExperience,
      ),
      serviceAccountsTask?.heatingType
        ? HEATING_SYSTEM_TO_DISPLAY_NAME[serviceAccountsTask?.heatingType]
        : 'N/A',
    ),
    getTaskDetailRow(
      text.disclosuresPage.serviceAccounts.serviceAccountCoolingType(
        offer.shouldSeeAgentExperience,
      ),
      serviceAccountsTask?.coolingType
        ? COOLING_SYSTEM_TO_DISPLAY_NAME[serviceAccountsTask?.coolingType]
        : 'N/A',
    ),
  ];

  const waterIssueRows = [
    getTaskDetailRow(
      text.disclosuresPage.serviceAccounts.hasWaterIssue(offer.shouldSeeAgentExperience),
      serviceAccountsTask?.hasExistingWaterIssue,
    ),
  ];

  if (serviceAccountsTask?.hasExistingWaterIssue) {
    waterIssueRows.push(
      getTaskDetailRow(
        text.disclosuresPage.serviceAccounts.waterIssueDetails,
        serviceAccountsTask?.hasExistingWaterIssueDetails,
      ),
    );
  }

  const sewerIssueRows = [
    getTaskDetailRow(
      text.disclosuresPage.serviceAccounts.hasSewerSepticIssue(offer.shouldSeeAgentExperience),
      serviceAccountsTask?.hasSewerSepticIssue,
    ),
  ];

  if (serviceAccountsTask?.hasSewerSepticIssue) {
    sewerIssueRows.push(
      getTaskDetailRow(
        text.disclosuresPage.serviceAccounts.septicIssueDetails,
        serviceAccountsTask?.hasSewerSepticIssueDetails,
      ),
    );
  }

  const utilitiesRows = [...heatingAndCoolingRows, ...waterIssueRows, ...sewerIssueRows];

  const disputeRows = [
    getTaskDetailRow(text.disclosuresPage.otherHomeInfo.hasDispute, otherDisclosures?.hasDispute),
  ];

  if (otherDisclosures?.hasDispute) {
    disputeRows.push(
      getTaskDetailRow(
        text.disclosuresPage.otherHomeInfo.disputeDetails,
        otherDisclosures?.hasDisputeDetails,
      ),
    );
  }

  const sharedPropertyRows = [
    getTaskDetailRow(
      text.disclosuresPage.otherHomeInfo.hasSharedProperty(offer.shouldSeeAgentExperience),
      otherDisclosures?.hasEncroachedOrSharedProperty,
    ),
  ];

  if (otherDisclosures?.hasEncroachedOrSharedProperty) {
    sharedPropertyRows.push(
      getTaskDetailRow(
        text.disclosuresPage.otherHomeInfo.sharedPropertyDetails,
        otherDisclosures?.hasEncrochedOrSharedPropertyDetails,
      ),
    );
  }

  const waterDamageRows = [
    getTaskDetailRow(
      text.disclosuresPage.otherHomeInfo.hasWaterDamage(offer.shouldSeeAgentExperience),
      otherDisclosures?.hasWaterDamage,
    ),
  ];

  if (otherDisclosures?.hasWaterDamage) {
    waterDamageRows.push(
      getTaskDetailRow(
        text.disclosuresPage.otherHomeInfo.waterDamageDetails,
        otherDisclosures?.hasWaterDamageDetails,
      ),
    );
  }

  const plumbingRepairsRows = [
    getTaskDetailRow(
      text.disclosuresPage.otherHomeInfo.hasRecentPlumbingRepairs(offer.shouldSeeAgentExperience),
      otherDisclosures?.hadPlumbingRepairs,
    ),
  ];

  if (otherDisclosures?.hadPlumbingRepairs) {
    plumbingRepairsRows.push(
      getTaskDetailRow(
        text.disclosuresPage.otherHomeInfo.recentPlumbingRepairsDetails,
        otherDisclosures?.hadPlumbingRepairsDetails,
      ),
    );
  }

  const occupancyRows = [
    getTaskDetailRow(
      text.disclosuresPage.otherHomeInfo.occupancyStatusAtAcquisition,
      otherDisclosures?.occupancyStatusAtAcquisition,
    ),
  ];

  const otherRows = [
    ...disputeRows,
    ...sharedPropertyRows,
    ...waterDamageRows,
    ...plumbingRepairsRows,
    ...occupancyRows,
  ];
  return (
    <WizardSummary
      rows={[...hvacRoofRows, ...utilitiesRows, ...otherRows]}
      trackingName="disclosures-form"
      onEdit={() => setIsEditingForm(true)}
      disableEdit={false}
    />
  );
};
